import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, orderBy, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { Layout } from '../components/layout/Layout';
import '../styles/sequencing-generator.css';
import '../styles/curriculum-manager.css';

const DIFF_CONFIG = {
  Beginner:     { color: '#10B981', emoji: '🌱' },
  Intermediate: { color: '#F59E0B', emoji: '📈' },
  Advanced:     { color: '#EF4444', emoji: '⭐' },
};

// ═══════════════════════════════════════════════════════════════════════════
// Create Activity — manual entry, no AI involved.
// Creates a draft document directly in Firestore, then hands off to the
// Activity Editor (/sequencing/review/:deckId) to add steps and images.
// ═══════════════════════════════════════════════════════════════════════════

export const SequencingGeneratorPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [title,        setTitle]        = useState('');
  const [description,  setDescription]  = useState('');
  const [category,     setCategory]     = useState('');
  const [subcategory,  setSubcategory]  = useState('');
  const [difficulty,   setDifficulty]   = useState('Beginner');
  const [tagsInput,    setTagsInput]    = useState('');

  const [existingCategories, setExistingCategories] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error,    setError]    = useState(null);

  // ── Pull existing category names for the datalist suggestions ────────────
  useEffect(() => {
    getDocs(query(collection(db, 'generatedSequencingDecks'), orderBy('category')))
      .then(snap => {
        const cats = [...new Set(snap.docs.map(d => d.data().category).filter(Boolean))];
        setExistingCategories(cats);
      })
      .catch(() => {});
  }, []);

  const canSubmit = title.trim().length > 0 && category.trim().length > 0;

  const handleCreate = async () => {
    if (!canSubmit || creating) return;
    setCreating(true);
    setError(null);

    try {
      const tags = tagsInput.split(',').map(t => t.trim()).filter(Boolean);

      const docRef = await addDoc(collection(db, 'generatedSequencingDecks'), {
        createdBy:   currentUser.uid,
        status:      'draft',
        title:       title.trim(),
        description: description.trim(),
        category:    category.trim(),
        subcategory: subcategory.trim(),
        difficulty,
        tags,
        steps:       [],
        stepCount:   0,
        createdAt:   serverTimestamp(),
        updatedAt:   serverTimestamp(),
      });

      navigate(`/sequencing/review/${docRef.id}`);
    } catch (err) {
      setError(err.message || 'Could not create the activity. Please try again.');
      setCreating(false);
    }
  };

  return (
    <Layout>
      <div className="gen-page">

        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="gen-header">
          <div className="gen-header-icon">📝</div>
          <div>
            <h1 className="gen-title">Create Sequencing Activity</h1>
            <p className="gen-subtitle">
              Fill in the basic details, then add steps and upload images on the next screen.
            </p>
          </div>
        </div>

        <div className="gen-input-card">

          {/* ── Title ────────────────────────────────────────────────── */}
          <label className="gen-label">Title</label>
          <input
            className="gen-input"
            style={{ fontSize: 16, marginBottom: 20 }}
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Brush Teeth"
            disabled={creating}
          />

          {/* ── Description ──────────────────────────────────────────── */}
          <label className="gen-label">Description</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={2}
            placeholder="Brief description of what this activity teaches (optional)"
            disabled={creating}
            style={{
              width: '100%', boxSizing: 'border-box', resize: 'vertical',
              fontSize: 14, fontFamily: 'inherit', padding: '10px 14px',
              background: '#F9FAFB', border: '2px solid #E5E7EB',
              borderRadius: 10, outline: 'none', color: '#111827',
              marginBottom: 20, lineHeight: 1.5,
            }}
          />

          {/* ── Category / Subcategory ───────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 }}>
            <div>
              <label className="gen-label">Category</label>
              <input
                list="existing-categories"
                value={category}
                onChange={e => setCategory(e.target.value)}
                placeholder="e.g. Daily Living Skills"
                disabled={creating}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  fontSize: 14, fontFamily: 'inherit', padding: '10px 14px',
                  background: '#F9FAFB', border: '2px solid #E5E7EB',
                  borderRadius: 10, outline: 'none', color: '#111827',
                }}
              />
              <datalist id="existing-categories">
                {existingCategories.map(c => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <label className="gen-label">Subcategory</label>
              <input
                value={subcategory}
                onChange={e => setSubcategory(e.target.value)}
                placeholder="e.g. Hygiene"
                disabled={creating}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  fontSize: 14, fontFamily: 'inherit', padding: '10px 14px',
                  background: '#F9FAFB', border: '2px solid #E5E7EB',
                  borderRadius: 10, outline: 'none', color: '#111827',
                }}
              />
            </div>
          </div>

          {/* ── Difficulty ───────────────────────────────────────────── */}
          <label className="gen-label">Difficulty</label>
          <div className="gen-diff-row" style={{ marginBottom: 20 }}>
            {Object.entries(DIFF_CONFIG).map(([level, cfg]) => (
              <button
                key={level}
                className={`gen-diff-btn ${difficulty === level ? 'gen-diff-btn--on' : ''}`}
                style={{ '--diff-color': cfg.color }}
                onClick={() => setDifficulty(level)}
                disabled={creating}
              >
                <span style={{ fontSize: 20 }}>{cfg.emoji}</span>
                <span className="gen-diff-label">{level}</span>
              </button>
            ))}
          </div>

          {/* ── Tags ─────────────────────────────────────────────────── */}
          <label className="gen-label">Tags</label>
          <input
            className="gen-input"
            style={{ fontSize: 14, marginBottom: 28 }}
            value={tagsInput}
            onChange={e => setTagsInput(e.target.value)}
            placeholder="comma, separated, tags (optional)"
            disabled={creating}
          />

          {/* ── Submit ───────────────────────────────────────────────── */}
          <button
            className="gen-cta"
            onClick={handleCreate}
            disabled={!canSubmit || creating}
          >
            {creating
              ? <><span className="gen-spinner" /> Creating…</>
              : '+ Create Draft & Add Steps'
            }
          </button>

          {error && <p className="gen-error">{error}</p>}

          {!canSubmit && (
            <p style={{ textAlign: 'center', fontSize: 12, color: '#D1D5DB', marginTop: 12 }}>
              Title and category are required to continue
            </p>
          )}
        </div>

      </div>
    </Layout>
  );
};
